import { TabGroup, TabList, Tab, TabPanels, TabPanel } from "@headlessui/react";
import {
  Button,
  Flyout,
  Heading,
  Label,
  LoadableContentContainer,
  PenIcon,
  PlusIcon,
  ScrollableContainer,
  SearchInput,
  TrashIcon,
  XmarkIcon,
} from "@wexinc/wex-ui";
import classNames from "classnames";
import Avatar from "components/user/Avatar";
import { produce } from "immer";
import { debounce, isEqual, sortBy } from "lodash";
import { useEffect, useMemo, useState } from "react";
import useCOPOrganizationService, {
  OrganizationDetails,
  OrganizationInvitation,
  OrganizationLineage,
  OrganizationMember,
} from "services/organization";
import { CopPermissionIds, Role, knownRoles, useCopSecurityService } from "services/security";
import OrganizationInvitesGrid, { InvitationActions, OnInvitationAction } from "./OrganizationInvitesGrid";
import OrganizationMemberRoleEditor from "./OrganizationMemberRoleEditor";

interface Props {
  /** This is only undefined for moments when its being loaded-up in the background.  */
  organizationInEdit?: OrganizationDetails;
  onEditOrganization: () => void;
  onDeleteOrganization: (organization: OrganizationDetails) => void;
  onClose: () => void;
  onNewOrganization: () => void;
  onAddMembers: () => void;
  onMemberDeleted: (removedMember: OrganizationMember) => void;
  onMemberUpdated: (updatedMember: OrganizationMember) => void;
  disallowNewOrganizationReason?: string;
  onInvitationAction: OnInvitationAction;
}

const EditOrganizationFlyout: React.FC<Props> = (props) => {
  const [organizationLineage, setOrganizationLineage] = useState<OrganizationLineage>();
  const copSecurityService = useCopSecurityService();
  const [isBusyMessage, setIsBusyMessage] = useState<string | undefined>();
  const [visibleMembers, setVisibleMembers] = useState<OrganizationMember[]>([]);
  const [invites, setInvites] = useState<OrganizationInvitation[]>([]);
  const [membersSearch, setMembersSearch] = useState<string>("");

  const {
    organizationInEdit,
    onClose,
    onEditOrganization,
    onDeleteOrganization,
    onNewOrganization,
    onAddMembers,
    onMemberDeleted,
    onMemberUpdated,
    disallowNewOrganizationReason,
    onInvitationAction,
  } = props;
  const copOrganizationService = useCOPOrganizationService();
  const [roleEditorIsVisible, setRoleEditorIsVisible] = useState(false);
  const [orgPerms, setOrgPerms] = useState<CopPermissionIds[]>([]);

  useEffect(() => {
    if (!organizationInEdit && !!organizationLineage) {
      setOrganizationLineage(undefined);
      setOrgPerms([]);
    }
    if (organizationInEdit && organizationLineage) {
      setIsBusyMessage(undefined);
    } else {
      setIsBusyMessage("Loading...");
      // Anytime the organizationInEdit changes we'll get the corresponding org lineage
      if (organizationInEdit) {
        copOrganizationService.getOrganizationLineage(organizationInEdit.id).then(setOrganizationLineage);
        copSecurityService
          .filterUserOrgPerms(organizationInEdit.id, [
            "editOrganization",
            "deleteOrganization",
            "createOrganization",
            "editMembers",
            "editRoles",
            "editOrgOwner",
          ])
          .then(setOrgPerms);
      }
    }
    setVisibleMembers((organizationInEdit && organizationInEdit.members) || []);

    setInvites((organizationInEdit && organizationInEdit.invitees) || []);
    // eslint-disable-next-line
  }, [organizationInEdit, organizationLineage]);

  const searchMembers = useMemo(
    () =>
      debounce(function search(searchTerm: string) {
        searchTerm = searchTerm.toLocaleLowerCase();
        setVisibleMembers(
          organizationInEdit!.members.filter((i) => i.user.fullName?.toLocaleLowerCase().indexOf(searchTerm) !== -1),
        );
        setInvites(organizationInEdit!.invitees.filter((i) => i.email.toLocaleLowerCase().indexOf(searchTerm) !== -1));
      }, 200),

    [organizationInEdit],
  );

  useEffect(() => {
    if (organizationInEdit) {
      searchMembers(membersSearch);
    }
  }, [membersSearch, organizationInEdit]);

  function hasPermission(permission: CopPermissionIds) {
    return orgPerms.includes(permission);
  }

  async function onEditMemberRoles(organizationMember: OrganizationMember, selectedRoles: Role[]) {
    setRoleEditorIsVisible(false);
    if (!hasPermission("editRoles")) {
      return;
    }
    if (
      isEqual(
        sortBy(organizationMember.roles, (i) => i.id),
        sortBy(selectedRoles, (i) => i.id),
      )
    ) {
      return;
    }

    setIsBusyMessage("Saving...");
    organizationMember = produce(organizationMember, (draft) => {
      draft.roles = selectedRoles;
    });
    await copOrganizationService.updateOrganizationMemberRoles(organizationInEdit!.id!, organizationMember);
    if (organizationMember.user.id === copSecurityService.securityStore.loggedInUser!.id) {
      await copSecurityService.updatePermissions();
    }
    setIsBusyMessage(undefined);
    onMemberUpdated(organizationMember);
  }

  async function onRemoveMember(organizationMember: OrganizationMember) {
    setRoleEditorIsVisible(false);
    setIsBusyMessage("Removing...");
    await copOrganizationService.deleteOrganizationMember(organizationInEdit!.id!, organizationMember);
    onMemberDeleted(organizationMember);
    setIsBusyMessage(undefined);
  }

  async function onInvitationActionInternal(action: InvitationActions, invitation: OrganizationInvitation) {
    setIsBusyMessage("Removing...");
    await copOrganizationService.revokeInvitation(organizationInEdit!.id, invitation.id);
    onInvitationAction(action, invitation);
    setIsBusyMessage(undefined);
  }

  const FlyoutHeader = () => {
    if (!organizationInEdit || !organizationLineage) {
      return null;
    }
    return (
      <>
        <Heading size="medium">{organizationInEdit!.name}</Heading>
        <div
          className="flex"
          data-test-id="edit-org-flyout-header"
        >
          {hasPermission("editOrganization") && (
            <Button
              testId="edit-org"
              buttonType="secondary-neutral"
              className="py-1 px-2"
              onClick={() => onEditOrganization()}
              blurOnClick
            >
              <PenIcon />
            </Button>
          )}
          <Button
            testId="close-flyout"
            buttonType="secondary-neutral"
            className="py-1 px-2"
            onClick={() => onClose()}
          >
            <XmarkIcon />
          </Button>
        </div>
      </>
    );
  };

  const FlyoutFooter = () => {
    if (!organizationInEdit || !organizationLineage) {
      return null;
    }
    if (!hasPermission("deleteOrganization") && !hasPermission("createOrganization")) {
      return null;
    }
    return (
      <>
        {hasPermission("deleteOrganization") && (
          <Button
            testId="delete-organization"
            buttonType="secondary-critical"
            onClick={() => onDeleteOrganization(organizationInEdit!)}
            disabled={organizationLineage.children.length > 0}
          >
            <TrashIcon className="fill-current mr-2" />
            Delete organization
          </Button>
        )}
        {hasPermission("createOrganization") && (
          <>
            <Button
              testId="new-organization"
              buttonType="tertiary"
              onClick={() => onNewOrganization()}
              disabled={!!disallowNewOrganizationReason}
            >
              <PlusIcon className="mr-2" />
              New organization
            </Button>
            {disallowNewOrganizationReason && (
              <Label data-test-id="max-org-levels-message">{disallowNewOrganizationReason}</Label>
            )}
          </>
        )}
      </>
    );
  };

  return (
    <>
      <Flyout
        className="w-[520px]"
        header={organizationInEdit && organizationLineage && <FlyoutHeader />}
        headerClassName="flex items-center justify-between"
        footer={<FlyoutFooter />}
        footerClassName="flex items-center justify-between"
      >
        <LoadableContentContainer
          showLoading={!!isBusyMessage}
          message={isBusyMessage}
        />
        {organizationInEdit && organizationLineage && (
          <>
            <div className="flex-grow overflow-hidden flex flex-col">
              {roleEditorIsVisible && (
                <div className="absolute opacity-50 inset-0 bg-neutral-dark-10 w-full h-full z-[1]"></div>
              )}
              <ScrollableContainer className="max-h-22">{organizationInEdit.description}</ScrollableContainer>
              {hasPermission("editMembers") && (
                <Button
                  testId="add-member"
                  buttonType="tertiary"
                  className="flex-grow-0 ml-auto my-2 mr-1"
                  onClick={() => onAddMembers()}
                >
                  <PlusIcon className="mr-2" />
                  Add members
                </Button>
              )}
              <TabGroup as="div">
                <TabList className="flex p-1 mb-4 space-x-1 rounded-md bg-primary-30 border-primary-40 border-2">
                  {[`Members (${visibleMembers?.length || 0})`, `Invited (${invites?.length || 0})`].map(
                    (tabName, index) => (
                      <Tab
                        key={`tab-${index}`}
                        data-test-id={`tab-${index}`}
                        className={({ selected }) =>
                          classNames(
                            "w-full py-2.5 leading-3 rounded-md focus:outline-none",
                            selected
                              ? "text-primary-50 font-semibold  bg-utility-white "
                              : "text-utility-white hover:bg-primary-20 hover:font-semibold hover:text-utility-white",
                          )
                        }
                      >
                        {tabName}
                      </Tab>
                    ),
                  )}
                </TabList>
                <TabPanels className="px-0.5">
                  <TabPanel key="members-panel">
                    {organizationInEdit.members.length > 0 && (
                      <SearchInput
                        onChange={(e) => setMembersSearch(e.currentTarget.value)}
                        name="search-members"
                        containerClassName="w-full mb-4"
                        placeholder="Search members"
                        value={membersSearch}
                      />
                    )}

                    <ul>
                      {visibleMembers.map((i) => (
                        <li
                          key={i.user.email}
                          className="flex items-center justify-between border-b border-b-neutral-dark-20 py-3 overflow-auto"
                        >
                          <div className="flex items-center">
                            <Avatar
                              className="mr-3"
                              text={i.user.initials}
                            />
                            {i.user.fullName}
                          </div>

                          <OrganizationMemberRoleEditor
                            className="z-20"
                            canEditRoles={hasPermission("editRoles")}
                            canAssignOrgOwnerRole={hasPermission("editOrgOwner")}
                            onOpen={() => setRoleEditorIsVisible(true)}
                            organizationMember={i}
                            onRemoveMember={
                              hasPermission("editMembers") &&
                              (!i.roles.find((i) => i.name === knownRoles.orgOwner) || hasPermission("editOrgOwner"))
                                ? onRemoveMember
                                : undefined
                            }
                            onClose={(selectedRoles) => onEditMemberRoles(i, selectedRoles)}
                          />
                        </li>
                      ))}
                    </ul>
                    {visibleMembers?.length === 0 && <Label className="text-center block mt-10">(No members)</Label>}
                  </TabPanel>
                  <TabPanel key="invitations-panel">
                    {organizationInEdit.invitees.length > 0 && (
                      <SearchInput
                        onChange={(e) => setMembersSearch(e.currentTarget.value)}
                        name="search-invitees"
                        containerClassName="w-full"
                        placeholder="Search invitees"
                        value={membersSearch}
                      />
                    )}

                    {invites?.length > 0 && (
                      <OrganizationInvitesGrid
                        onInvitationAction={onInvitationActionInternal}
                        canPerformInviteActions={hasPermission("editMembers")}
                        organizationInvites={invites}
                      />
                    )}

                    {invites?.length === 0 && <Label className="text-center block mt-10">(No invitations)</Label>}
                  </TabPanel>
                </TabPanels>
              </TabGroup>
              <div className="flex justify-between"></div>
            </div>
          </>
        )}
      </Flyout>
    </>
  );
};

export default EditOrganizationFlyout;
